import React from 'react'
import { Container } from 'typescript-ioc/es5'
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { CATALOG_CONTEXT_TRIGGERS, TriggerType } from '@pushly/models/lib/enums/journeys'
import { antdStaticApi } from '../../../providers/antd-app-provider/antd-static-api'
import { JourneyService } from '../../../services'
import { DomainDto } from '../../../dtos/domain'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import { simpleNotification } from '../../../_utils/utils'
import { getDefaultMaxAbandonedItemAgeDays } from '../utils/defaults'
import type { Journey, LiveJourneyStatusName } from '../types/journey-api-response'

export const JOURNEY_PAUSE_CONFIRMATION_COPY =
    "New subscribers won't enter. Subscribers already in the journey are held and receive nothing until you resume."

/** A journey as the pause and resume entry points hold it. List rows carry no revision or steps. */
export type JourneyForStatusChange = Pick<Journey, 'id' | 'name' | 'configuration'> & Partial<Pick<Journey, 'revision'>>

interface IJourneyStatusChangeConfirmationOptions {
    journey: JourneyForStatusChange
    targetStatus: LiveJourneyStatusName
    domain: DomainDto | undefined
    /** Runs when the person confirms. The confirm button shows progress until it settles. */
    onConfirm: () => void | Promise<unknown>
}

interface IJourneyStatusChangeOptions extends Omit<IJourneyStatusChangeConfirmationOptions, 'onConfirm'> {
    /** Runs after the API accepted the new status, so the caller can reload what it shows. */
    onStatusChanged: () => void | Promise<unknown>
}

function resolveJourneyTriggerType(journey: JourneyForStatusChange): TriggerType | undefined {
    return extractJourneyTrigger(journey as Journey)?.configuration?.type ?? journey.configuration?.computed_trigger_type
}

/**
 * How many days back an item-state trigger reaches when its journey resumes: the trigger's own
 * max_item_age_days, else the domain's default. A journey without its steps is fetched. Resolves
 * undefined when that fetch fails, so the confirmation can still say what resuming does without
 * stating a reach it could not read.
 */
export async function resolveQualifyingItemMaxAgeDays(
    journey: JourneyForStatusChange,
    domain: DomainDto | undefined,
): Promise<number | undefined> {
    let journeyWithSteps: JourneyForStatusChange | undefined = journey.revision?.steps ? journey : undefined

    if (!journeyWithSteps) {
        const journeyService = Container.get(JourneyService)
        const res = await journeyService.fetchById(journey.id, { showLoadingScreen: true })
        if (!res.ok || !res.data) {
            return undefined
        }

        journeyWithSteps = res.data
    }

    const triggerMaxItemAgeDays = extractJourneyTrigger(journeyWithSteps as Journey)?.configuration?.params
        ?.max_item_age_days

    return triggerMaxItemAgeDays ?? getDefaultMaxAbandonedItemAgeDays(domain)
}

export function buildJourneyPauseConfirmationContent(): React.ReactNode {
    return <p>{JOURNEY_PAUSE_CONFIRMATION_COPY}</p>
}

export function buildJourneyResumeConfirmationContent(
    entersEveryoneWhoCurrentlyQualifies: boolean,
    qualifyingItemMaxAgeDays: number | undefined,
): React.ReactNode {
    let qualifyingSubscribersCopy: string | undefined
    if (entersEveryoneWhoCurrentlyQualifies) {
        qualifyingSubscribersCopy =
            qualifyingItemMaxAgeDays === undefined
                ? 'Resuming will enter everyone who currently qualifies.'
                : `Resuming will enter everyone who currently qualifies, including items updated in the last ${qualifyingItemMaxAgeDays} ${
                      qualifyingItemMaxAgeDays === 1 ? 'day' : 'days'
                  }.`
    }

    return (
        <>
            <p>Subscribers held in the journey continue where they were paused, and new subscribers can enter again.</p>
            {qualifyingSubscribersCopy && <p>{qualifyingSubscribersCopy}</p>}
        </>
    )
}

/**
 * Asks the person to confirm pausing or resuming a journey, and runs onConfirm if they do.
 * Resolves once the dialog is open, not when it is answered.
 */
export async function confirmJourneyStatusChange({
    journey,
    targetStatus,
    domain,
    onConfirm,
}: IJourneyStatusChangeConfirmationOptions): Promise<void> {
    const isPause = targetStatus === 'PAUSED'

    let content: React.ReactNode
    if (isPause) {
        content = buildJourneyPauseConfirmationContent()
    } else {
        const triggerType = resolveJourneyTriggerType(journey)
        // Catalog-context triggers evaluate entry from each subscriber's current item state rather than from a new
        // event, so resuming one of these enters everyone who qualifies at that moment, exactly as launching it
        // would, and the resume confirmation states how far back qualifying items reach.
        const entersEveryoneWhoCurrentlyQualifies = !!triggerType && CATALOG_CONTEXT_TRIGGERS.includes(triggerType)
        const qualifyingItemMaxAgeDays = entersEveryoneWhoCurrentlyQualifies
            ? await resolveQualifyingItemMaxAgeDays(journey, domain)
            : undefined

        content = buildJourneyResumeConfirmationContent(entersEveryoneWhoCurrentlyQualifies, qualifyingItemMaxAgeDays)
    }

    antdStaticApi.modal.confirm({
        title: isPause ? 'Pause Journey' : 'Resume Journey',
        className: 'journey-status-change-confirm',
        icon: isPause ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
        okText: isPause ? 'Yes, Pause' : 'Yes, Resume',
        cancelText: 'Cancel',
        width: 425,
        content: (
            <div>
                <p>
                    <b>{journey.name}</b>
                </p>
                {content}
            </div>
        ),
        onOk: () => onConfirm(),
    })
}

/** Confirms, then sends only the new status. Used where status is the only thing changing. */
export async function confirmAndApplyJourneyStatusChange({
    journey,
    targetStatus,
    domain,
    onStatusChanged,
}: IJourneyStatusChangeOptions): Promise<void> {
    return confirmJourneyStatusChange({
        journey,
        targetStatus,
        domain,
        onConfirm: async () => {
            const journeyService = Container.get(JourneyService)
            const res = await journeyService.patch(
                journey.id,
                { status: targetStatus },
                { showLoadingScreen: true, cancellationKey: `journey-status-change-${journey.id}` },
            )

            // JourneyService has already shown the API's error when the request failed.
            if (!res.ok) {
                return
            }

            simpleNotification(
                'success',
                <span>
                    Journey <strong>{journey.name}</strong> {targetStatus === 'PAUSED' ? 'paused' : 'resumed'}.
                </span>,
            )

            await onStatusChanged()
        },
    })
}
